import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, HelpCircle, FileText } from 'lucide-react';

export default function QuoteGenSupport() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Support</h1>
            <p className="text-zinc-400 text-lg">QuoteGen - AI Quote Generator for Contractors</p>
          </div>

          {/* Contact Card */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-8 mb-10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-zinc-800 rounded-lg">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Contact Us</h2>
                <p className="text-zinc-400 mb-4">
                  Have a question, feedback, or need help with QuoteGen? We're here to help!
                </p>
                <a 
                  href="mailto:parkerdevwork@gmail.com?subject=QuoteGen Support"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  parkerdevwork@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How does QuoteGen work?</h3>
                <p className="text-zinc-400">
                  QuoteGen uses AI to help you create professional quotes for construction and contractor work. 
                  Simply enter your project details, materials, and labor information, and the app will generate 
                  a polished, professional quote you can share with clients.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Do I need to create an account?</h3>
                <p className="text-zinc-400">
                  No! QuoteGen works without any account or sign-up. Just download the app and start 
                  creating quotes immediately.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Is my data safe?</h3>
                <p className="text-zinc-400">
                  Yes. We don't collect any personal information. Your quotes are stored locally on your 
                  device only. For more details, please read our{' '}
                  <Link 
                    to="/quotegen/privacy-policy" 
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Privacy Policy
                  </Link>.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">The app isn't working properly. What should I do?</h3>
                <p className="text-zinc-400">
                  Try these steps first:
                </p>
                <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1 ml-2">
                  <li>Make sure you have a stable internet connection</li>
                  <li>Close and reopen the app</li>
                  <li>Update to the latest version from the App Store</li>
                  <li>Restart your device</li>
                </ul>
                <p className="text-zinc-400 mt-3">
                  If the issue persists, please contact us with details about what's happening.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Can I use QuoteGen offline?</h3>
                <p className="text-zinc-400">
                  QuoteGen requires an internet connection to generate quotes since it uses AI processing. 
                  However, any quotes you've already created are stored locally and can be viewed offline.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How do I request a feature or report a bug?</h3>
                <p className="text-zinc-400">
                  We'd love to hear from you! Send us an email at{' '}
                  <a 
                    href="mailto:parkerdevwork@gmail.com?subject=QuoteGen Feedback"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    parkerdevwork@gmail.com
                  </a>{' '}
                  with your suggestions or bug reports. Your feedback helps us make QuoteGen better!
                </p>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="mt-12 pt-8 border-t border-zinc-800/50">
            <div className="flex flex-wrap gap-6">
              <Link 
                to="/quotegen/privacy-policy"
                className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <FileText className="w-4 h-4" />
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 QuoteGen. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
